import { useState, useEffect, useRef } from 'react'
import {
  getModelMedia,
  syncModelMedia,
  cancelSyncModelMedia,
  describeMediaPhotos,
  downloadAllFullMedia,
  downloadMediaFull,
  subscribeMediaStatus,
  getApiErrorMessage,
} from '../api'
import { useToast } from '../contexts/ToastContext'
import { useI18n } from '../i18n'
import { applyMediaUpdate } from '../lib/mediaPresentation'

/**
 * Загрузка медиа, синхронизация, SSE-обновления.
 *
 * @param {string|number} modelId
 * @param {object|null} model  — нужен для отслеживания смены модели
 * @param {function} onModelUpdate  — callback для обновления полей model (sync_status и т.д.)
 */
export default function useMediaSync(modelId, model, onModelUpdate, mediaParams = {}) {
  const { t } = useI18n()
  const { addToast } = useToast()
  const [media, setMedia] = useState(null)
  const [mediaTotal, setMediaTotal] = useState(0)
  const [mediaHasMore, setMediaHasMore] = useState(false)
  const [loadingMedia, setLoadingMedia] = useState(false)
  const [describingPhotos, setDescribingPhotos] = useState(false)
  const [mediaError, setMediaError] = useState('')
  const [downloadQueueCount, setDownloadQueueCount] = useState(0)
  const mediaUpdatesRef = useRef(Object.create(null))
  const mediaParamsKey = JSON.stringify(mediaParams || {})
  const scopeKey = `${modelId}:${mediaParamsKey}`
  const activeScope = useRef(scopeKey)
  activeScope.current = scopeKey
  const updatesModelId = useRef(modelId)
  if (String(updatesModelId.current) !== String(modelId)) {
    mediaUpdatesRef.current = Object.create(null)
    updatesModelId.current = modelId
  }
  const loadSequence = useRef(0)

  function loadMedia() {
    if (!modelId) return
    const sequence = ++loadSequence.current
    const isCurrent = () => activeScope.current === scopeKey && sequence === loadSequence.current
    setLoadingMedia(true)
    setMediaError('')
    getModelMedia(modelId, mediaParams || {})
      .then((r) => {
        if (!isCurrent()) return
        const inner = r?.data ?? r
        const list = Array.isArray(inner?.list) ? inner.list : (Array.isArray(inner) ? inner : [])
        const updates = mediaUpdatesRef.current
        const merged = list.map((m) => (updates[m.id] ? { ...m, ...updates[m.id] } : m))
          .filter((m) => mediaParams.include_hidden || !m.is_hidden)
        setMedia(merged)
        setMediaTotal(typeof inner?.total === 'number' ? inner.total : merged.length)
        setMediaHasMore(Boolean(inner?.hasMore))
        if (typeof inner?.download_queue_count === 'number') {
          setDownloadQueueCount(inner.download_queue_count)
        }
      })
      .catch((err) => {
        if (!isCurrent()) return
        setMedia([])
        setMediaTotal(0)
        setMediaHasMore(false)
        setMediaError(err?.message || 'Ошибка загрузки медиа')
      })
      .finally(() => { if (isCurrent()) setLoadingMedia(false) })
  }

  async function handleSyncMedia(downloadFullMedia = false) {
    if (!modelId) return
    onModelUpdate?.({ error: '' })
    try {
      await syncModelMedia(modelId, downloadFullMedia, mediaParams || {})
      onModelUpdate?.({ media_sync_status: 'syncing' })
      addToast(t('toast.sync.started'), 'success')
      setTimeout(() => loadMedia(), 1500)
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.syncFailed'))
      onModelUpdate?.({ error: msg })
    }
  }

  async function handleDownloadAllFullMedia() {
    if (!modelId) return
    onModelUpdate?.({ error: '' })
    try {
      const r = await downloadAllFullMedia(modelId, mediaParams || {})
      const count = r?.queued ?? 0
      addToast(t('media.downloadAll.queued', { count }), 'success')
      setTimeout(() => loadMedia(), 500)
    } catch (e) {
      const msg = getApiErrorMessage(e, t('errors.media.downloadAll'))
      onModelUpdate?.({ error: msg })
    }
  }

  async function handleDescribePhotos({ overwrite = false } = {}) {
    if (!modelId) return
    onModelUpdate?.({ error: '' })
    setDescribingPhotos(true)
    try {
      const r = await describeMediaPhotos(modelId, {
        ...(mediaParams || {}),
        limit: mediaParams?.limit || 50,
        overwrite,
      })
      const queued = Number(r?.queued || 0)
      const skipped = r?.skipped || {}
      const details = [
        skipped.already_described ? `${skipped.already_described} already described` : '',
        skipped.missing_file ? `${skipped.missing_file} missing file` : '',
      ].filter(Boolean).join(', ')
      addToast(
        queued
          ? `Queued ${queued} photo descriptions${details ? ` (${details})` : ''}`
          : `No photos queued${details ? ` (${details})` : ''}`,
        queued ? 'success' : 'info',
      )
      setTimeout(() => loadMedia(), 2500)
      setTimeout(() => loadMedia(), 8000)
    } catch (e) {
      const msg = getApiErrorMessage(e, 'Failed to queue photo descriptions')
      onModelUpdate?.({ error: msg })
    } finally {
      setDescribingPhotos(false)
    }
  }

  async function handleDownloadOneFullMedia(mediaId) {
    if (!modelId || !mediaId) return
    try {
      await downloadMediaFull(modelId, mediaId)
      loadMedia()
    } catch {
      /* Тихая ошибка — пользователь увидит статус в списке */
    }
  }

  async function handleCancelSyncMedia() {
    if (!modelId) return
    onModelUpdate?.({ error: '' })
    try {
      await cancelSyncModelMedia(modelId)
      onModelUpdate?.({
        media_sync_status: 'failed',
        media_sync_error: 'Остановлено пользователем',
        media_sync_updated_at: new Date().toISOString(),
      })
      addToast(t('toast.sync.stopped'), 'success')
    } catch (e) {
      const msg = getApiErrorMessage(e, t('toast.error.stopSyncFailed'))
      onModelUpdate?.({ error: msg })
    }
  }

  function updateMediaItem(updated) {
    if (!updated?.id || activeScope.current !== scopeKey) return
    // Discard GETs started before this acknowledged edit. Never cache full
    // rows: stale session/group bindings would undo a later script removal.
    loadSequence.current += 1
    setLoadingMedia(false)
    delete mediaUpdatesRef.current[updated.id]
    setMedia((prev) => applyMediaUpdate(prev, updated, !!mediaParams.include_hidden))
  }

  useEffect(() => {
    activeScope.current = scopeKey
    setMedia(null)
    setMediaTotal(0)
    setMediaHasMore(false)
    if (model) loadMedia()
    return () => { activeScope.current = null }
  }, [model?.id, mediaParamsKey])

  useEffect(() => {
    if (!modelId || !model) return
    const unsubscribe = subscribeMediaStatus(modelId, (data) => {
      if (String(updatesModelId.current) !== String(modelId)) return
      if (data.media_sync_status != null) {
        onModelUpdate?.({
          media_sync_status: data.media_sync_status,
          media_sync_error: data.media_sync_error,
          media_sync_updated_at: data.media_sync_updated_at,
        })
      }
      if (typeof data.download_queue_count === 'number') {
        setDownloadQueueCount(data.download_queue_count)
      }
      const mid = data.media_id
      if (mid == null) return
      mediaUpdatesRef.current[mid] = { ...mediaUpdatesRef.current[mid], ...data }
      setMedia((prev) => {
        if (!prev) return prev
        return prev.map((m) => (m.id === mid ? { ...m, ...data } : m))
      })
    })
    return unsubscribe
  }, [modelId, model?.id])

  return {
    media,
    setMedia,
    mediaTotal,
    mediaHasMore,
    loadingMedia,
    describingPhotos,
    mediaError,
    downloadQueueCount,
    mediaUpdatesRef,
    loadMedia,
    handleSyncMedia,
    handleCancelSyncMedia,
    handleDownloadAllFullMedia,
    handleDescribePhotos,
    handleDownloadOneFullMedia,
    updateMediaItem,
  }
}
